import type { LiveTranscriptionSession } from '@/shared/api/transcriptor';

import { LIVE_AUDIO_QUEUE_LIMIT } from '../config';
import { encodePcm16Base64, LIVE_PCM_SAMPLE_RATE } from '../services';

import type { LiveTranscriptionEvent } from './liveTranscript';

// OpenAI Realtime transcription over a browser WebSocket. Everything specific
// to the provider stays in this module; the rest of the live mode sees
// LiveTranscriptionEvent only.
//
// The OpenAI realtime contract this module speaks: a `transcription`
// session with `gpt-live-transcribe`, 24 kHz `audio/pcm`, `turn_detection`
// null (the model has no server VAD, the client commits each phrase),
// base64 PCM in `input_audio_buffer.append`, text in
// `conversation.item.input_audio_transcription.delta` / `.completed`. A browser
// authenticates the WebSocket with the ephemeral secret as a subprotocol.

export type RealtimeTranscriptionHandlers = {
  onOpen: () => void;
  onEvent: (event: LiveTranscriptionEvent) => void;
  onProviderError: (message: string) => void;
  onClose: () => void;
};

export type RealtimeTranscriptionConnection = {
  appendAudio: (samples: Int16Array) => void;
  commit: () => void;
  clear: () => void;
  close: () => void;
};

// The provider accepts ISO 639-1 codes and regional zh codes, not BCP 47
// tags such as en-US.
export const toProviderLanguage = (language: string) => {
  const normalized = language.trim().toLowerCase().replace('_', '-');
  return normalized.startsWith('zh-') ? normalized : normalized.split('-')[0];
};

export const buildTranscriptionSessionUpdate = ({
  model,
  language,
}: {
  model: string;
  language: string;
}) => ({
  type: 'session.update',
  session: {
    type: 'transcription',
    audio: {
      input: {
        format: { type: 'audio/pcm', rate: LIVE_PCM_SAMPLE_RATE },
        transcription: {
          model,
          ...(language && { languages: [toProviderLanguage(language)] }),
        },
        turn_detection: null,
      },
    },
  },
});

type ProviderEvent = {
  type?: unknown;
  item_id?: unknown;
  delta?: unknown;
  transcript?: unknown;
  error?: { message?: unknown };
};

export type ParsedRealtimeEvent =
  | LiveTranscriptionEvent
  | { type: 'error'; message: string }
  | null;

const readProviderEvent = (data: unknown): ProviderEvent | null => {
  if (typeof data !== 'string') {
    return null;
  }
  try {
    return JSON.parse(data) as ProviderEvent;
  } catch {
    return null;
  }
};

export const parseRealtimeEvent = (data: unknown): ParsedRealtimeEvent => {
  const event = readProviderEvent(data);
  const itemId = typeof event?.item_id === 'string' ? event.item_id : '';
  switch (event?.type) {
    case 'conversation.item.input_audio_transcription.delta':
      return itemId && typeof event.delta === 'string'
        ? { type: 'partial', itemId, text: event.delta }
        : null;
    case 'conversation.item.input_audio_transcription.completed':
      return itemId && typeof event.transcript === 'string'
        ? { type: 'completed', itemId, text: event.transcript }
        : null;
    case 'conversation.item.input_audio_transcription.failed':
      return itemId ? { type: 'failed', itemId } : null;
    case 'input_audio_buffer.committed':
      return itemId ? { type: 'committed', itemId } : null;
    case 'error':
      return {
        type: 'error',
        message:
          typeof event.error?.message === 'string' ? event.error.message : '',
      };
    default:
      return null;
  }
};

// Audio captured while the socket is still opening is sent once it opens.

export const openRealtimeTranscription = ({
  session,
  handlers,
}: {
  session: LiveTranscriptionSession;
  handlers: RealtimeTranscriptionHandlers;
}): RealtimeTranscriptionConnection => {
  const socket = new WebSocket(session.connectUrl, [
    'realtime',
    `openai-insecure-api-key.${session.clientSecret}`,
  ]);
  const queue: string[] = [];
  const lifecycle = { closedByClient: false };

  const send = (message: object) => {
    const payload = JSON.stringify(message);
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(payload);
      return;
    }
    if (socket.readyState === WebSocket.CONNECTING) {
      queue.push(payload);
      queue.splice(0, Math.max(0, queue.length - LIVE_AUDIO_QUEUE_LIMIT));
    }
  };

  socket.addEventListener('open', () => {
    socket.send(
      JSON.stringify(
        buildTranscriptionSessionUpdate({
          model: session.model,
          language: session.language,
        }),
      ),
    );
    queue.splice(0).forEach((payload) => socket.send(payload));
    handlers.onOpen();
  });
  socket.addEventListener('message', (message: MessageEvent) => {
    const event = parseRealtimeEvent(message.data);
    if (!event) {
      return;
    }
    if (event.type === 'error') {
      handlers.onProviderError(event.message);
      return;
    }
    handlers.onEvent(event);
  });
  socket.addEventListener('close', () => {
    if (!lifecycle.closedByClient) {
      handlers.onClose();
    }
  });

  return {
    appendAudio: (samples) =>
      send({
        type: 'input_audio_buffer.append',
        audio: encodePcm16Base64(samples),
      }),
    commit: () => send({ type: 'input_audio_buffer.commit' }),
    clear: () => send({ type: 'input_audio_buffer.clear' }),
    close: () => {
      lifecycle.closedByClient = true;
      queue.splice(0);
      socket.close();
    },
  };
};
